// @ts-strict-ignore
import {
	CcpaSignalPayload,
	communicationService,
	GdprConsentPayload,
} from '@ad-engine/communication';
import {
	context,
	DEFAULT_MAX_DELAY,
	externalLogger,
	targetingService,
	Tcf,
	tcf,
	trackingOptIn,
	UniversalStorage,
	Usp,
	usp,
	utils,
} from '@ad-engine/core';
import Cookies from 'js-cookie';
import { A9_APSTAG_HEM_SENT } from '../../communication/events/events-bidders';
import { A9Bid, A9BidConfig, A9CCPA, ApstagConfig, ApstagTokenConfig } from '../a9/types';

const logGroup = 'a9-apstag';

export class Apstag {
	public static AMAZON_TOKEN_TTL = 14 * 24 * 60 * 60 * 1000; // 14 days
	private static instance: Apstag;

	static make(): Apstag {
		if (!Apstag.instance) {
			Apstag.reset();
		}

		return Apstag.instance;
	}

	/**
	 * @deprecated
	 * Used in unit tests.
	 */
	static reset(): Apstag {
		Apstag.instance = new Apstag();

		return Apstag.instance;
	}

	private script: Promise<Event>;
	private renderImpEndCallbacks = [];
	storage: UniversalStorage;
	utils = utils;
	tcf: Tcf = tcf;
	usp: Usp = usp;

	private constructor() {
		this.storage = new UniversalStorage();
		this.insertScript();
		this.configure();
		this.addRenderImpHook();
	}

	private insertScript(): void {
		this.script = utils.timedPartnerScriptLoader.loadScriptWithStatus(
			'//c.amazon-adsystem.com/aax2/apstag.js',
			logGroup,
			true,
			'first',
		);
	}

	public hasRecord(): boolean {
		return !!this.storage.getItem('apstagRecord');
	}

	public getRecord(): string {
		return this.storage.getItem('apstagRecord');
	}

	public sendMediaWikiHEM(): void {
		const userEmailHashes: [string, string, string] = context.get('wiki.opts.userEmailHashes');
		if (!Array.isArray(userEmailHashes) || userEmailHashes?.length !== 3) {
			return;
		}
		const record = userEmailHashes[2];
		this.sendHEM(record);
	}

	public async sendHEM(
		record: string,
		consents?: GdprConsentPayload & CcpaSignalPayload,
	): Promise<void> {
		if (!record) {
			utils.warner(logGroup, 'Trying to send HEM without source record');
			return;
		}

		// If delete/cleanup flag is being set, do not create new Amazon Tokens.
		if (context.get('bidders.a9.hem.cleanup')) {
			return;
		}

		// Amazon Tokens feature disabled.
		if (!context.get('bidders.a9.hem.enabled')) {
			return;
		}

		try {
			await this.script;
			const optOut =
				!trackingOptIn.isOptedIn(consents?.gdprConsent) ||
				trackingOptIn.isOptOutSale(consents?.ccpaSignal);
			const optOutString = optOut ? '1' : '0';
			const amazonTokenCreated = !!this.storage.getItem('apstagHEMsent', true);
			const amazonTokenExpired =
				amazonTokenCreated && this.storage.getItem('apstagHEMsent', true) < Date.now().toString();
			const userConsentHasChanged =
				this.storage.getItem('apstagHEMoptOut', true) &&
				optOutString !== this.storage.getItem('apstagHEMoptOut', true);
			// make sure TCF API is available before sending HEM
			let gdprConfig;
			if (this.tcf.exists) {
				gdprConfig = trackingOptIn.isGdprConsentRequired()
					? { gdpr: { enabled: true, consent: (await this.tcf.getTCData())?.tcString } }
					: {};
			} else {
				gdprConfig = {};
			}
			const tokenConfig: ApstagTokenConfig = {
				hashedRecords: [{ type: 'email', record }],
				optOut,
				...gdprConfig,
			};
			if (userConsentHasChanged) {
				utils.logger(logGroup, 'Updating user consents', tokenConfig, 'optOut', optOut);
				window.apstag.upa(tokenConfig);
			} else if (!amazonTokenCreated || amazonTokenExpired) {
				utils.logger(logGroup, 'Sending HEM to apstag', tokenConfig, 'optOut', optOut);
				window.apstag.rpa(tokenConfig);
			}
			// Necessary for updating optOut status.
			this.storage.setItem('apstagRecord', record);
			this.storage.setItem('apstagHEMsent', (Date.now() + Apstag.AMAZON_TOKEN_TTL).toString());
			this.storage.setItem('apstagHEMoptOut', optOutString);
			communicationService.emit(A9_APSTAG_HEM_SENT);
		} catch (e) {
			utils.logger(logGroup, 'Error sending HEM to apstag', e);
		}
	}

	private configure(): void {
		window.apstag = window.apstag || { _Q: [] };

		if (typeof window.apstag.init === 'undefined') {
			window.apstag.init = (...args) => {
				this.configureCommand('i', args);
			};
		}

		if (typeof window.apstag.fetchBids === 'undefined') {
			window.apstag.fetchBids = (...args) => {
				this.configureCommand('f', args);
			};
		}
	}

	private async addRenderImpHook(): Promise<void> {
		await this.script;

		const original = window.apstag.renderImp;

		window.apstag.renderImp = (...options) => {
			original(...options);
			const [doc, impId] = options;
			this.renderImpEndCallbacks.forEach((cb) => cb(doc, impId));
		};
	}

	private configureCommand(command, args): void {
		window.apstag._Q.push([command, args]);
	}

	async init(): Promise<void> {
		let signalData = null;

		if (this.usp.exists && utils.getCountryCode() === 'US') {
			signalData = await this.usp.getSignalData();
		}

		const apsConfig = this.getApstagConfig(signalData);
		await this.script;
		window.apstag.init(apsConfig);

		if (context.get('bidders.a9.hem.cleanup')) {
			if (Cookies.get('AMZN-Token') || this.storage.getItem('apstagRecord')) {
				utils.logger(logGroup, 'Cleaning Amazon Token...');
				window.apstag.dpa();
				this.storage.removeItem('apstagRecord');
				this.storage.removeItem('apstagHEMsent');
				this.storage.removeItem('apstagHEMoptOut');
				Cookies.remove('AMZN-Token', {
					path: '/',
				});
				return;
			} else {
				utils.logger(logGroup, 'Amazon Token already cleaned');
			}
		}

		// Check if consent has not been changed between page views.
		if (this.hasRecord()) {
			this.sendHEM(this.getRecord());
		}

		this.sendMediaWikiHEM();
	}

	async fetchBids(bidsConfig: A9BidConfig): Promise<A9Bid[]> {
		await this.script;

		if (trackingOptIn.isGdprConsentRequired() && tcf.exists) {
			// make sure TCF API is available before fetching bids
			await this.tcf.getTCData();
		}

		return new Promise<A9Bid[]>((resolve) => {
			window.apstag.fetchBids(bidsConfig, (currentBids) => resolve(currentBids));
		});
	}

	async targetingKeys(): Promise<string[]> {
		await this.script;

		return window.apstag.targetingKeys();
	}

	async enableDebug(): Promise<void> {
		await this.script;
		window.apstag.debug('enable');
	}

	async disableDebug(): Promise<void> {
		await this.script;
		window.apstag.debug('disable');
	}

	/**
	 * Executes callback each time after apstag.renderImp is called
	 */
	async onRenderImpEnd(callback: (doc: HTMLDocument, impId: string) => void): Promise<void> {
		if (typeof callback !== 'function') {
			throw new Error('onRenderImpEnd used with callback not being a function');
		}
		this.renderImpEndCallbacks.push(callback);
	}

	private getApstagConfig(signalData: SignalData): ApstagConfig {
		const amazonId = context.get('bidders.a9.amazonId');
		const ortb2 = targetingService.get('openrtb2', 'openrtb2') ?? {};
		externalLogger.log('openrtb2 signals', { signals: JSON.stringify(ortb2) });

		return {
			pubID: amazonId,
			videoAdServer: 'DFP',
			deals: true,
			gdpr: {
				cmpTimeout: DEFAULT_MAX_DELAY,
			},
			...Apstag.getCcpaIfApplicable(signalData),
			signals: { ortb2 },
		};
	}

	private static getCcpaIfApplicable(signalData: SignalData): Partial<A9CCPA> {
		if (signalData && signalData.uspString) {
			return {
				params: {
					us_privacy: signalData.uspString,
				},
			};
		}

		return {};
	}
}
